import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { EllipsisVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import type { Member } from '@/shared/types/member';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';

import { Plugin } from '../../types';

const eventLabelMapper = (event: string): string => {
  switch (event) {
    case 'dialog:expired':
      return t('plugins.dialog_expired.label');
    case 'dialog:insights':
      return t('plugins.dialog_insights.label');
    default:
      return event;
  }
};

interface ColumnsProps {
  onEdit: (plugin: Plugin) => void;
  onDelete: (plugin: Plugin) => void;
  onToggleActive: (plugin: Plugin) => void;
  getMember: (userId: string) => Member | undefined;
  canWrite: boolean;
  canDelete: boolean;
}

export const createColumns = ({
  onEdit,
  onDelete,
  onToggleActive,
  getMember,
  canWrite,
  canDelete,
}: ColumnsProps): ColumnDef<Plugin>[] => [
  {
    id: 'name',
    accessorKey: 'name',
    ...COLUMN_PRESETS.LG,
    meta: { preset: 'LG', className: 'pl-4' },
    header: ({ column }) => <SortableHeader column={column}>{t('common.name')}</SortableHeader>,
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <span className="min-w-0 truncate font-medium">{row.original.name}</span>
        <RowActionIndicator action="edit" />
      </div>
    ),
  },
  {
    id: 'url',
    accessorKey: 'url',
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX' },
    enableSorting: false,
    header: () => t('plugins.url'),
    cell: ({ row }) => <span className="block truncate">{row.original.url}</span>,
  },
  {
    id: 'events',
    accessorFn: (row) => row.events.map((event) => eventLabelMapper(event)).join(', '),
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
    header: ({ column }) => <SortableHeader column={column}>{t('common.type')}</SortableHeader>,
    cell: ({ row }) => {
      const { events } = row.original;
      const [first, ...rest] = events;
      return (
        <div className="flex items-center gap-1">
          {first && (
            <Badge type="secondary" flavour="neutral" rounded={false}>
              {eventLabelMapper(first)}
            </Badge>
          )}
          {rest.length > 0 && (
            <InfoTooltip
              content={rest.map((event) => eventLabelMapper(event)).join(', ')}
              side="top"
            >
              <Badge type="secondary" flavour="neutral" rounded={false}>
                +{rest.length}
              </Badge>
            </InfoTooltip>
          )}
        </div>
      );
    },
  },
  {
    id: 'created_by',
    accessorFn: (row) => {
      const member = row.created_by ? getMember(row.created_by) : undefined;
      return member?.name || '';
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('common.created_by')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const createdBy = row.original.created_by;
      if (!createdBy) {
        return <span className="text-foreground-muted">-</span>;
      }
      const member = getMember(createdBy);
      const fallback = member?.name?.charAt(0) || '?';
      return (
        <div className="flex items-center gap-2">
          <Avatar size="xs">
            <AvatarImage src={member?.avatar || ''} alt={member?.name || ''} />
            <AvatarFallback>{fallback}</AvatarFallback>
          </Avatar>
          <span className="truncate">{member?.name || '-'}</span>
        </div>
      );
    },
  },
  {
    id: 'created',
    accessorKey: 'created',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
    header: ({ column }) => <SortableHeader column={column}>{t('common.created')}</SortableHeader>,
    cell: ({ row }) => {
      const created = row.original.created;
      return created ? (
        <TimestampTooltip timestamp={created}>{timeFromNow(created)}</TimestampTooltip>
      ) : (
        '-'
      );
    },
  },
  {
    id: 'active',
    accessorKey: 'active',
    enableSorting: false,
    ...COLUMN_PRESETS.MD,
    size: 112,
    maxSize: 128,
    meta: { preset: 'MD' },
    header: () => t('common.enabled'),
    cell: ({ row }) => (
      <Switch
        checked={row.original.active}
        onCheckedChange={() => onToggleActive(row.original)}
        disabled={!canWrite}
        onClick={(e) => e.stopPropagation()}
      />
    ),
  },
  {
    id: 'actions',
    enableHiding: false,
    enableSorting: false,
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
    cell: ({ row }) => {
      const plugin = row.original;
      return (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost-neutral"
              size="icon-md"
              className="h-8 w-8"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <EllipsisVerticalIcon size={16} className="text-foreground-muted" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem
              onSelect={() => onEdit(plugin)}
              className="flex items-center gap-2"
              disabled={!canWrite}
            >
              <PencilIcon size={16} className="text-foreground-muted" />
              {t('common.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => onDelete(plugin)}
              variant="destructive"
              className="flex items-center gap-2"
              disabled={!canDelete}
            >
              <Trash2Icon size={16} />
              {`${t('common.delete')}…`}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
